import { normalizePatientCommunicationPrefs } from '@/lib/patientCommunication';

/**
 * Parse/serialize the localStorage copy of `user_ui_preferences` used for first
 * paint on the patient page (tab order, hidden tabs, Patientoplysninger collapsed,
 * tab-order lock). The server row replaces this once loaded.
 */

export interface PatientUiPrefs {
  order: string[] | null;
  hidden: string[];
  patientInfoCollapsed: boolean;
  /** true = tabs cannot be dragged. Default locked. */
  tabsLocked: boolean;
  /** Fullscreen journal editor: show previous-notes panel. Default shown. */
  journalFullscreenShowHistory: boolean;
}

export const EMPTY_PATIENT_UI_PREFS: PatientUiPrefs = {
  order: null,
  hidden: [],
  patientInfoCollapsed: true,
  tabsLocked: true,
  journalFullscreenShowHistory: true,
};

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string');

/** Default prefs (no custom tabs, panel closed, tabs locked) are not stored. */
export function isDefaultPatientUiPrefs(prefs: PatientUiPrefs): boolean {
  return (
    (prefs.order?.length ?? 0) === 0 &&
    prefs.hidden.length === 0 &&
    prefs.patientInfoCollapsed &&
    prefs.tabsLocked &&
    prefs.journalFullscreenShowHistory
  );
}

/**
 * Read the cached prefs JSON. Accepts:
 * - the current `{order, hidden, patientInfoCollapsed, tabsLocked}` object
 * - the `{order, hidden, patientInfoCollapsed}` object (lock defaults to true)
 * - the #93 `{order, hidden}` object (collapsed defaults to true / closed; lock true)
 * - the #92 order-only array
 */
export function parsePatientUiPrefsCache(raw: string | null | undefined): PatientUiPrefs {
  if (!raw) return EMPTY_PATIENT_UI_PREFS;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isStringArray(parsed)) {
      return normalizePatientCommunicationPrefs({ ...EMPTY_PATIENT_UI_PREFS, order: parsed });
    }
    if (parsed && typeof parsed === 'object') {
      const { order, hidden, patientInfoCollapsed, tabsLocked, journalFullscreenShowHistory } =
        parsed as {
          order?: unknown;
          hidden?: unknown;
          patientInfoCollapsed?: unknown;
          tabsLocked?: unknown;
          journalFullscreenShowHistory?: unknown;
        };
      return normalizePatientCommunicationPrefs({
        order: isStringArray(order) ? order : null,
        hidden: isStringArray(hidden) ? hidden : [],
        patientInfoCollapsed:
          typeof patientInfoCollapsed === 'boolean' ? patientInfoCollapsed : true,
        tabsLocked: tabsLocked !== false,
        journalFullscreenShowHistory: journalFullscreenShowHistory !== false,
      });
    }
  } catch {
    // ignore unreadable cache
  }
  return EMPTY_PATIENT_UI_PREFS;
}

/** JSON to store, or null when the cache key should be removed. */
export function serializePatientUiPrefsCache(prefs: PatientUiPrefs): string | null {
  if (isDefaultPatientUiPrefs(prefs)) return null;
  return JSON.stringify({
    order: prefs.order,
    hidden: prefs.hidden,
    patientInfoCollapsed: prefs.patientInfoCollapsed,
    tabsLocked: prefs.tabsLocked,
    journalFullscreenShowHistory: prefs.journalFullscreenShowHistory,
  });
}
